<script>
import baseMixin from '@/mixins/baseMixin'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import $dayjs from '@/services/date'
import GenerateOverdueInvoiceChargesDTO from '@/models/finance/generateOverdueInvoiceChargesDTO'
import GenerateOverdueInvoiceChargesClientDTO from '@/models/finance/generateOverdueInvoiceChargesClientDTO'
import GenerateOverdueInvoiceChargesInvoiceDTO from '@/models/finance/generateOverdueInvoiceChargesInvoiceDTO'
import InvoiceCenterIdentifier from '@/constants/finance/InvoiceCenterIdentifier'
import OverdueInvoicesClientDialog from './overdue-invoices-client-dialog.vue'
import InterestChargesDialog from './interest-charges-dialog.vue'
import OverdueInvoicesConfirmDialog from './overdue-invoices-confirm-dialog.vue'
import InterestInvoicesCreatedDialog from './interest-invoices-created-dialog.vue'

export default {
  name: 'OverdueInvoicesByClientList',
  components: {
    OverdueInvoicesClientDialog,
    InterestChargesDialog,
    OverdueInvoicesConfirmDialog,
    InterestInvoicesCreatedDialog,
  },
  mixins: [baseMixin],
  props: {},
  data: (vm) => {
    return {
      isLoadingInvoiceCenter: false,
      isLoadingOverdueInvoices: false,
      overdueInvoicesByClientList: [],
      headers: [
        { text: '', value: '_selected', sortable: false },
        {
          text: vm.$t('finance.overdueInvoices.columnHeadings.clientName'),
          value: '_name',
        },
        {
          text: vm.$t(
            'finance.overdueInvoices.columnHeadings.overdueInvoiceCount'
          ),
          value: '_overdueInvoiceCount',
          filterable: false,
          align: 'end',
        },
        {
          text: vm.$t('finance.overdueInvoices.columnHeadings.invoiceValue'),
          value: 'totalInvoiceValueExTax',
          filterable: false,
          align: 'end',
        },
        {
          text: vm.$t(
            'finance.overdueInvoices.columnHeadings.outstandingInterest'
          ),
          value: 'existingInterestUnpaidExTax',
          filterable: false,
          align: 'end',
        },
        {
          text: vm.$t(
            'finance.overdueInvoices.columnHeadings.unchargedInterest'
          ),
          value: 'unchargedInterestExTax',
          filterable: false,
          align: 'end',
          cellClass: 'font-weight-bold',
        },
      ],
      openedClient: null,
      showClientDialog: false,
      openedInterestChargesDialogTitle: '',
      openedInterestCharges: [],
      showInterestChargesDialog: false,
      search: '',
      isSelectAll: false,
      showConfirmGenerateDialog: false,
      isGeneratingInvoices: false,
      dueDate: $dayjs().startOf('day').format('YYYY-MM-DD'),
      dueDatePickerMenu: false,
      onlyShowClientsWithUnchargedInterest: true,
      showInvoicesCreatedDialog: false,
      newInvoicesCreatedResult: null,
    }
  },
  computed: {
    ...mapGetters({
      invoiceCenterLocale: 'finance/currentInvoiceCenterLocale',
      invoiceCenterTimeZone: 'finance/currentInvoiceCenterTimeZone',
      invoiceCenter: 'finance/currentInvoiceCenter',
    }),
    selectedRows() {
      return this.filteredRows.filter((x) => x._selected)
    },
    filteredRows() {
      if (this.onlyShowClientsWithUnchargedInterest) {
        return this.overdueInvoicesByClientList.filter(
          (x) => x.unchargedInterestExTax > 0
        )
      }
      return [...this.overdueInvoicesByClientList]
    },
    selectedRowCount() {
      return this.selectedRows.length
    },
    selectableRowCount() {
      return this.overdueInvoicesByClientList.filter((x) => x._isSelectable)
        .length
    },
    totalUnchargedInterestValueExTax() {
      return this.overdueInvoicesByClientList.reduce(
        (total, current) => total + current.unchargedInterestExTax,
        0
      )
    },
    totalSelectedUnchargedInterestValueExTax() {
      return this.selectedRows.reduce(
        (total, current) => total + current.unchargedInterestExTax,
        0
      )
    },
    totalOverdueInvoiceCount() {
      return this.overdueInvoicesByClientList.reduce(
        (total, current) => total + current.overdueInvoices.length,
        0
      )
    },
    totalOverdueInvoiceValueExTax() {
      return this.overdueInvoicesByClientList.reduce(
        (total, current) => total + current.totalInvoiceValueExTax,
        0
      )
    },
    totalUnpaidInterestValueExTax() {
      return this.overdueInvoicesByClientList.reduce(
        (total, current) => total + current.existingInterestUnpaidExTax,
        0
      )
    },
    dueDateFormatted() {
      return this.$d(
        $dayjs(this.dueDate),
        'dateFormatted',
        this.invoiceCenterLocale
      )
    },
    dueDateUTC() {
      if (!this.invoiceCenter) return null
      else
        return $dayjs(this.dueDate)
          .tz(this.invoiceCenterTimeZone, true)
          .toISOString()
    },
    showGenerateInterestChargesPanel() {
      return this.selectedRows.length > 0
    },
    isLoading() {
      return this.isLoadingInvoiceCenter || this.isLoadingOverdueInvoices
    },
  },
  watch: {
    isSelectAll(newVal) {
      this.toggleSelectAll(newVal)
    },
  },
  async created() {
    await this.loadInvoiceCenter()
    await this.loadList()
  },
  mounted() {
    this.$refs.search.focus()
  },
  destroyed() {
    this.clearCurrentInvoiceCenter()
  },
  methods: {
    ...mapActions({
      getInvoiceCenterByID: 'finance/getInvoiceCenterByID',
      getOverdueInvoicesByClient: 'finance/getOverdueInvoicesByClient',
      generateOverdueInvoiceCharges: 'finance/generateOverdueInvoiceCharges',
    }),
    ...mapMutations({
      setCurrentInvoiceCenter: 'finance/SET_CURRENT_INVOICE_CENTER',
      clearCurrentInvoiceCenter: 'finance/CLEAR_CURRENT_INVOICE_CENTER',
    }),
    async loadInvoiceCenter() {
      this.isLoadingInvoiceCenter = true

      try {
        const invoiceCenterResponse = await this.getInvoiceCenterByID(
          InvoiceCenterIdentifier.TEACHING_USA
        )
        if (invoiceCenterResponse.isSuccess) {
          this.setCurrentInvoiceCenter(invoiceCenterResponse.data)
        }
      } finally {
        this.isLoadingInvoiceCenter = false
      }
    },
    async loadList() {
      if (!this.invoiceCenter) return

      this.isLoadingOverdueInvoices = true

      const filters = {
        invoiceCenterID: this.invoiceCenter.id,
        dueDateUTC: this.dueDateUTC,
      }

      try {
        const response = await this.getOverdueInvoicesByClient(filters)

        if (response.isSuccess) {
          this.overdueInvoicesByClientList = response.data.map((x) => ({
            ...x,
            _id: x.isGroup ? `group_${x.clientGroupID}` : x.clientID,
            _name: x.isGroup ? x.clientGroupName : x.clientName,
            _overdueInvoiceCount: x.overdueInvoices.length,
            _isSelectable: x.unchargedInterestExTax !== 0,
            _selected: false,
          }))
        }
      } finally {
        this.isLoadingOverdueInvoices = false
      }
    },
    onRowClick(data) {
      this.openedClient = data
      this.showClientDialog = true
    },
    onCloseClientDialog() {
      this.showClientDialog = false
      this.openedClient = null
    },
    onViewExistingChargesClick(client) {
      this.openedInterestCharges = client.overdueInvoices
        .map((overdueInvoice) => overdueInvoice.existingCharges)
        .flat()
      this.openedInterestChargesDialogTitle = client._name
      this.showInterestChargesDialog = true
    },
    onCloseInterestChargesDialog() {
      this.showInterestChargesDialog = false
      this.openedInterestCharges = []
    },
    onGenerateClick() {
      this.showConfirmGenerateDialog = true
    },
    async generateInvoices() {
      this.isGeneratingInvoices = true

      const payload = new GenerateOverdueInvoiceChargesDTO({
        invoiceCenterID: this.invoiceCenter.id,
        dueDateUTC: this.dueDateUTC,
        selectedClients: this.selectedRows.map(
          (x) =>
            new GenerateOverdueInvoiceChargesClientDTO({
              clientID: x.clientID,
              clientGroupID: x.clientGroupID,
              expectedUnchargedInterestExTax: x.unchargedInterestExTax,
              expectedOverdueInvoices: x.overdueInvoices.map(
                (i) =>
                  new GenerateOverdueInvoiceChargesInvoiceDTO({
                    overdueInvoiceID: i.invoiceID,
                    expectedUnchargedInterestExTax: i.unchargedInterestExTax,
                  })
              ),
            })
        ),
      })

      const response = await this.generateOverdueInvoiceCharges(payload)
      if (response.isSuccess) {
        this.newInvoicesCreatedResult = response.data
        this.showInvoicesCreatedDialog = true
        this.showConfirmGenerateDialog = false
        this.loadList()
      }
      this.isGeneratingInvoices = false
    },
    toggleSelectAll(value) {
      this.overdueInvoicesByClientList = this.overdueInvoicesByClientList.map(
        (x) => ({ ...x, _selected: x._isSelectable ? value : false })
      )
    },
    rowClasses({ _selected, isGroup }) {
      return `${_selected ? 'v-data-table__selected ' : ''}${
        isGroup ? 'client-group' : 'client'
      } `
    },
    onCloseConfirmGenerateDialog() {
      this.showConfirmGenerateDialog = false
    },
    datePickerInput(i) {
      this.dueDatePickerMenu = false
      this.loadList()
    },
    onCloseInvoicesCreatedDialog() {
      this.showInvoicesCreatedDialog = false
    },
    onClientSelect(client) {
      const foundIndex = this.overdueInvoicesByClientList.findIndex(
        (c) => c._id === client._id
      )
      if (foundIndex === -1) return
      this.overdueInvoicesByClientList.splice(foundIndex, 1, {
        ...client,
        _selected: !client._selected,
      })
    },
    generateClientUrl(clientID) {
      return `${this.reliefRosterUrl}/client/viewClient/${clientID}`
    },
    generateClientGroupUrl(clientGroupID) {
      return `${this.reliefRosterUrl}/client/viewGroup/${clientGroupID}`
    },
  },
}
</script>

<template>
  <div>
    <BasePageTitle
      :title="$t('finance.overdueInvoices.featureName')"
    ></BasePageTitle>
    <v-row>
      <v-col cols="8">
        <v-row>
          <v-col>
            <v-text-field
              ref="search"
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              clearable
              class="mt-3 mr-5"
            ></v-text-field>
            <v-checkbox
              v-model="onlyShowClientsWithUnchargedInterest"
              class="mt-0"
              dense
              :label="
                $t(
                  'finance.overdueInvoices.onlyShowClientsWithUnchargedInterestLabel'
                )
              "
            ></v-checkbox>
          </v-col>
          <v-col>
            <v-menu
              ref="dueDatePickerMenu"
              v-model="dueDatePickerMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dueDateFormatted"
                  :label="$t('finance.overdueInvoices.dueDateLabel')"
                  prepend-icon="mdi-calendar"
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dueDate"
                no-title
                @input="datePickerInput"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>

        <v-data-table
          class="overdue-invoices-table elevation-1"
          :headers="headers"
          :items="filteredRows"
          :item-class="rowClasses"
          :search="search"
          item-key="_id"
          :loading="isLoading"
          :loading-text="
            $t('finance.overdueInvoices.loadingOverdueInvoicesTableMessage')
          "
          :sort-by="['totalInvoiceValueExTax']"
          :sort-desc="[true]"
          :items-per-page="20"
          :footer-props="{ 'items-per-page-options': [10, 20, 50, 100, -1] }"
          @click:row="onRowClick"
        >
          <template v-slot:header._selected>
            <v-simple-checkbox
              id="select-all-check"
              v-model="isSelectAll"
              :ripple="false"
            ></v-simple-checkbox>
          </template>
          <template v-slot:item._selected="{ item }">
            <v-simple-checkbox
              v-if="item._isSelectable"
              data-testid="select-client-check"
              :value="item._selected"
              :ripple="false"
              @input="onClientSelect(item)"
            ></v-simple-checkbox>
          </template>
          <template v-slot:item._name="{ item }">
            <v-icon v-if="item.isGroup" small class="mr-1"
              >mdi-home-group</v-icon
            >
            <BaseLink
              v-if="isEmbedded && item.isGroup"
              :href="generateClientGroupUrl(item.clientGroupID)"
              allow-insecure
              @click.stop
              >{{ item._name }}</BaseLink
            >
            <BaseLink
              v-else-if="isEmbedded"
              :href="generateClientUrl(item._id)"
              allow-insecure
              @click.stop
              >{{ item._name }}</BaseLink
            >
            <span v-else data-testid="client-name">{{ item._name }}</span>
          </template>
          <template v-slot:item.totalInvoiceValueExTax="{ item }">
            <i18n-n
              data-testid="total-invoice-value-ex-tax"
              :value="item.totalInvoiceValueExTax"
              format="currency"
              :locale="invoiceCenterLocale"
            ></i18n-n>
          </template>
          <template v-slot:item.unchargedInterestExTax="{ item }">
            <i18n-n
              data-testid="uncharged-interest-ex-tax"
              :value="item.unchargedInterestExTax"
              format="currency"
              :locale="invoiceCenterLocale"
            ></i18n-n>
          </template>
          <template v-slot:item.existingInterestUnpaidExTax="{ item }">
            <i18n-n
              v-if="item.existingInterestChargedExTax > 0"
              :value="item.existingInterestChargedExTax"
              format="currency"
              :locale="invoiceCenterLocale"
            ></i18n-n>
            <span v-else>-</span>
            <div v-if="item.existingInterestInvoicesCount > 0" class="caption">
              <a
                data-testid="view-charges-link"
                href="#"
                @click.prevent.stop="onViewExistingChargesClick(item)"
                >{{ $t('finance.overdueInvoices.viewChargesLinkText') }}</a
              ></div
            >
          </template>
        </v-data-table>
      </v-col>
      <v-col>
        <v-card>
          <v-card-title>
            {{ $t('finance.overdueInvoices.summaryCardTitle') }}
            <v-spacer></v-spacer>
          </v-card-title>
          <v-card-text>
            <v-simple-table dense>
              <template v-slot>
                <tbody>
                  <tr>
                    <td
                      >{{
                        $t(
                          'finance.overdueInvoices.totalUnchargedInterestLabel'
                        )
                      }}
                      <v-tooltip right>
                        <template v-slot:activator="{ on }">
                          <v-icon small color="info" v-on="on"
                            >mdi-help-circle</v-icon
                          >
                        </template>
                        <span>{{
                          $t(
                            'finance.overdueInvoices.totalUnchargedInterestTooltip'
                          )
                        }}</span>
                      </v-tooltip>
                    </td>
                    <td class="text-right font-weight-bold">
                      <i18n-n
                        :value="totalUnchargedInterestValueExTax"
                        format="currency"
                        :locale="invoiceCenterLocale"
                      ></i18n-n
                    ></td>
                  </tr>
                  <tr>
                    <td
                      >{{
                        $t(
                          'finance.overdueInvoices.totalOutstandingInterestLabel'
                        )
                      }}
                      <v-tooltip right>
                        <template v-slot:activator="{ on }">
                          <v-icon small color="info" v-on="on"
                            >mdi-help-circle</v-icon
                          >
                        </template>
                        <span>{{
                          $t(
                            'finance.overdueInvoices.totalOutstandingInterestTooltip'
                          )
                        }}</span>
                      </v-tooltip>
                    </td>
                    <td class="text-right">
                      <i18n-n
                        :value="totalUnpaidInterestValueExTax"
                        format="currency"
                        :locale="invoiceCenterLocale"
                      ></i18n-n
                    ></td>
                  </tr>
                  <tr>
                    <td>{{
                      $t(
                        'finance.overdueInvoices.totalOverdueInvoiceValueLabel'
                      )
                    }}</td>
                    <td class="text-right"
                      ><i18n-n
                        :value="totalOverdueInvoiceValueExTax"
                        format="currency"
                        :locale="invoiceCenterLocale"
                      ></i18n-n
                    ></td>
                  </tr>
                  <tr>
                    <td>{{
                      $t(
                        'finance.overdueInvoices.totalOverdueInvoiceCountLabel'
                      )
                    }}</td>
                    <td class="text-right"
                      ><i18n-n
                        :value="totalOverdueInvoiceCount"
                        :locale="invoiceCenterLocale"
                      ></i18n-n
                    ></td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-card>
        <v-card
          v-if="showGenerateInterestChargesPanel"
          class="generate-interest-charges-panel mt-4"
        >
          <v-card-title>
            {{ $t('finance.overdueInvoices.generateInterestChargesCardTitle') }}
          </v-card-title>
          <v-card-text>
            <v-simple-table dense>
              <template v-slot>
                <tbody>
                  <tr>
                    <td>{{
                      $t('finance.overdueInvoices.selectedClientsCountLabel')
                    }}</td>
                    <td class="text-right">
                      <i18n-n
                        data-testid="clients-selected-count"
                        :value="selectedRowCount"
                        :locale="invoiceCenterLocale"
                      ></i18n-n>
                      /
                      <i18n-n
                        data-testid="selectable-clients-count"
                        :value="selectableRowCount"
                        :locale="invoiceCenterLocale"
                      ></i18n-n> </td
                  ></tr>
                  <tr>
                    <td
                      >{{
                        $t(
                          'finance.overdueInvoices.totalUnchargedInterestLabel'
                        )
                      }}
                      <v-tooltip right>
                        <template v-slot:activator="{ on }">
                          <v-icon small color="info" v-on="on"
                            >mdi-help-circle</v-icon
                          >
                        </template>
                        <span>{{
                          $t(
                            'finance.overdueInvoices.totalUnchargedInterestForSelectedClientsTooltip'
                          )
                        }}</span>
                      </v-tooltip>
                    </td>
                    <td class="text-right font-weight-bold">
                      <i18n-n
                        data-testid="total-uncharged-interest"
                        :value="totalSelectedUnchargedInterestValueExTax"
                        format="currency"
                        :locale="invoiceCenterLocale"
                      ></i18n-n
                    ></td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="onGenerateClick">{{
              $t('finance.overdueInvoices.generateInterestChargesCardBtnText')
            }}</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <OverdueInvoicesClientDialog
      v-if="openedClient !== null"
      :show-dialog="showClientDialog"
      :client="openedClient"
      @close-dialog="onCloseClientDialog"
    />
    <InterestChargesDialog
      :show-dialog="showInterestChargesDialog"
      :charges="openedInterestCharges"
      :title="openedInterestChargesDialogTitle"
      @close-dialog="onCloseInterestChargesDialog"
    />
    <OverdueInvoicesConfirmDialog
      :show-dialog="showConfirmGenerateDialog"
      :total-uncharged-interest-value-ex-tax="
        totalSelectedUnchargedInterestValueExTax
      "
      :selected-client-count="selectedRowCount"
      :locale="invoiceCenterLocale"
      :is-generating-invoices="isGeneratingInvoices"
      @close-dialog="onCloseConfirmGenerateDialog"
      @confirm="generateInvoices"
    />
    <InterestInvoicesCreatedDialog
      v-if="newInvoicesCreatedResult"
      :show-dialog="showInvoicesCreatedDialog"
      :new-invoices="newInvoicesCreatedResult.newInvoices"
      :new-invoice-run="newInvoicesCreatedResult.invoiceRun"
      :title="openedInterestChargesDialogTitle"
      @close-dialog="onCloseInvoicesCreatedDialog"
    />
  </div>
</template>

<style lang="scss" scoped>
::v-deep .overdue-invoices-table tr td:hover {
  cursor: pointer;
}
</style>
